import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Plus, Zap, MapPin, Calendar, ChevronRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrivateEvents = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPrivateEvents = async () => {
      try {
        const response = await axios.get(`${API}/private-events`, {
          headers: getAuthHeaders()
        });
        setEvents(response.data || []);
      } catch (error) {
        console.error('Failed to fetch private events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPrivateEvents();
  }, [getAuthHeaders]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/hub')}
                data-testid="back-to-hub-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Zap className="h-6 w-6 text-violet-600" />
                <h1 className="text-xl font-bold">My Private Events</h1>
                <Badge variant="secondary">{events.length} Events</Badge>
              </div>
            </div>
            <Button
              onClick={() => navigate('/get-it-done?action=create-event')}
              className="rounded-full"
              data-testid="create-private-event-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Event
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : events.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="pt-6 text-center py-12">
              <Zap className="h-12 w-12 text-violet-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No private events yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first private event to start planning
              </p>
              <Button
                onClick={() => navigate('/get-it-done?action=create-event')}
                className="rounded-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Private Event
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {events.map((event) => (
              <Card
                key={event.id}
                className="hover:shadow-lg transition-all duration-300 cursor-pointer border-2 hover:border-violet-300"
                onClick={() => navigate(`/get-it-done?eventId=${event.id}`)}
                data-testid={`private-event-card-${event.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-base font-semibold line-clamp-2">
                      {event.name}
                    </CardTitle>
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {(event.city || event.state) && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                      <MapPin className="h-3 w-3" />
                      <span>{event.city}{event.city && event.state ? ', ' : ''}{event.state}</span>
                    </div>
                  )}
                  {event.eventDate && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      <span>{new Date(event.eventDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  {event.description && (
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                      {event.description}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default PrivateEvents;
